import { Paper } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "../../components/Snackbar";
import { WorkspaceHeader } from "../../components/WorkspaceHeader";
import { useWorkspaceStore } from "../../store/workspace/WorkspaceStore";
import { CompanyTypeFilter } from "../Workspace/UserFilters/CompanyTypeFilter";
import { CompetitorsSelection } from "../Workspace/WorkspaceConfiguration/AdvancedConfiguration/CompetitorsSelection";
import { WhitespaceConfiguration } from "../Workspace/WorkspaceConfiguration/AdvancedConfiguration/WhitespaceConfiguration";
import { useConfigTabNavigation } from "../Workspace/WorkspaceConfiguration/hooks/useConfigTabNavigation";
import { ChartsSection } from "./ChartsSection";
import { MarketSizingConfigView } from "./MarketSizingConfigView";
import { SettingsConfigurationView } from "./SettingsConfigurationView";
import { NEW_WORKSPACE_TEXT } from "./constants";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`market-sizing-tabpanel-${index}`}
      aria-labelledby={`market-sizing-tab-${index}`}
      sx={{ pt: 2, height: "100%", minHeight: 0, boxSizing: "border-box" }}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `market-sizing-tab-${index}`,
    "aria-controls": `market-sizing-tabpanel-${index}`,
  };
}

export const WorkspaceConfigurationPage = () => {
  const workspace = useWorkspaceStore((state) => state.draft);
  const { showInfo } = useSnackbar();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source");

  useEffect(() => {
    if (
      workspace?.id &&
      ["newWorkspace", "onboarding"].includes(source || "")
    ) {
      showInfo(NEW_WORKSPACE_TEXT);
    }
  }, [source, showInfo, workspace?.id]);

  // Use the custom hook for tab navigation
  const { activeTabIndex, handleTabChange } = useConfigTabNavigation();

  if (!workspace) {
    return <></>;
  }

  return (
    <Box
      id="workspace-configuration-page"
      padding="30px"
      display="flex"
      flexDirection="column"
      flexGrow={1}
      height="100%"
    >
      <Box mb={3}>
        <WorkspaceHeader title="Market Sizing Configuration" />
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        borderTop={1}
        borderBottom={1}
        borderColor="grey.300"
        padding={1}
        mb={2}
      >
        <CompanyTypeFilter />
      </Stack>

      <Stack direction="column" flexGrow={1} height="100%" minHeight={0}>
        <Paper sx={{ zIndex: 10, position: "sticky", top: 0 }} elevation={0}>
          <Box borderTop={1} borderColor="divider">
            <Tabs
              value={activeTabIndex}
              onChange={(_e, value) => handleTabChange(value)}
              aria-label="market sizing configuration tabs"
              slotProps={{
                indicator: {
                  sx: {
                    top: 0,
                  },
                },
              }}
            >
              <Tab label="Market Sizing" {...a11yProps(0)} />
              <Tab label="Whitespace" {...a11yProps(1)} />
              <Tab label="Competitors" {...a11yProps(2)} />
              <Tab label="Settings" {...a11yProps(3)} />
            </Tabs>
          </Box>
        </Paper>

        <Box display="flex" height="100%" minHeight={0} width={"100%"}>
          <Grid
            width={"500px"}
            flexShrink={0}
            sx={{
              height: "100%",
              minHeight: 0,
            }}
          >
            <TabPanel value={activeTabIndex} index={0}>
              <MarketSizingConfigView />
            </TabPanel>
            <TabPanel value={activeTabIndex} index={1}>
              <WhitespaceConfiguration />
            </TabPanel>
            <TabPanel value={activeTabIndex} index={2}>
              <CompetitorsSelection />
            </TabPanel>
            <TabPanel value={activeTabIndex} index={3}>
              <SettingsConfigurationView />
            </TabPanel>
          </Grid>
          <Box
            display={activeTabIndex === 0 ? "flex" : "none"}
            height="100%"
            width={"100%"}
            boxSizing="border-box"
          >
            {workspace && <ChartsSection />}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
